/* eslint react-hooks/exhaustive-deps: "warn" */

import { useMemo, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Icon } from 'ui/atoms/Icon/Icon';
import { Section } from 'ui/molecules/Section/Section';

import styles from './DataSourcesDirectory.module.scss';

import { PAGE_SIZE, REGION_ORDER, REGISTRY_SOURCES, getFrequencyLabel } from './DataSourcesDirectory.data';
import { RegistrySource } from './DataSourcesDirectory.types';

const getPageWindow = (current: number, total: number): (number | '…')[] => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: (number | '…')[] = [1];
  if (current > 3) {
    pages.push('…');
  }

  for (let page = Math.max(2, current - 1); page <= Math.min(total - 1, current + 1); page += 1) {
    pages.push(page);
  }

  if (current < total - 2) {
    pages.push('…');
  }
  pages.push(total);

  return pages;
};

export const DataSourcesDirectory = () => {
  const { t } = useInt();
  const [query, setQuery] = useState('');
  const [region, setRegion] = useState<string>('All');
  const [page, setPage] = useState(1);

  const frequencyLabel = getFrequencyLabel(t);

  const regionCounts = useMemo(() => {
    const counts: Record<string, number> = { All: REGISTRY_SOURCES.length };
    REGISTRY_SOURCES.forEach((row) => {
      counts[row.region] = (counts[row.region] ?? 0) + 1;
    });
    return counts;
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return REGISTRY_SOURCES.filter((row) => {
      if (region !== 'All' && row.region !== region) {
        return false;
      }
      if (!q) {
        return true;
      }
      return (
        row.country.toLowerCase().includes(q) ||
        row.region.toLowerCase().includes(q) ||
        (row.source1?.toLowerCase().includes(q) ?? false) ||
        (row.source2?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [query, region]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * PAGE_SIZE;
  const pageRows = filtered.slice(start, start + PAGE_SIZE);

  const goToPage = (next: number) => {
    setPage(next);
    document.getElementById('data-sources-table')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleRegionSelect = (next: string) => {
    setRegion(next);
    setPage(1);
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setPage(1);
  };

  return (
    <Section className={styles.root}>
      <div className={styles.controls}>
        <label className={styles.search}>
          <Icon type="search" size="small" />
          <input
            type="text"
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            placeholder={t('Search by country, region, or source name…')}
            autoComplete="off"
          />
        </label>

        <div className={styles.pills}>
          {REGION_ORDER.map((item) => (
            <button
              key={item}
              type="button"
              className={cn(styles.pill, region === item && styles.pillActive)}
              onClick={() => handleRegionSelect(item)}
            >
              {t(item)}
              <span className={styles.pillCount}>{regionCounts[item] ?? 0}</span>
            </button>
          ))}
        </div>
      </div>

      <div id="data-sources-table" className={styles.tableWrap}>
        <p className={styles.info}>
          {filtered.length === 0 ? (
            t('0 sources found')
          ) : (
            <>
              {t('Showing')}{' '}
              <b>
                {start + 1}–{Math.min(start + PAGE_SIZE, filtered.length)}
              </b>{' '}
              {t('of')} <b>{filtered.length}</b> {t('sources')}
            </>
          )}
        </p>

        {pageRows.length > 0 ? (
          <div className={styles.scroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('Jurisdiction')}</th>
                  <th>{t('Region')}</th>
                  <th>{t('Company Registry Source')}</th>
                  <th>{t('Tax / Revenue Authority')}</th>
                  <th>{t('Refresh')}</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row: RegistrySource) => (
                  <tr key={`${row.country}-${row.region}`}>
                    <td className={styles.jurisdiction}>
                      <span className={styles.flag}>{row.flag}</span>
                      {row.country}
                    </td>
                    <td>
                      <span className={styles.regionBadge}>{t(row.region)}</span>
                    </td>
                    <td className={styles.source}>{row.source1 ?? <span className={styles.dash}>—</span>}</td>
                    <td className={styles.source}>{row.source2 ?? <span className={styles.dash}>—</span>}</td>
                    <td>
                      <span className={cn(styles.freq, styles[`freq${row.frequency}`])}>
                        <span className={styles.freqDot} />
                        {frequencyLabel[row.frequency]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={styles.empty}>
            <Icon type="search" size="big" />
            <p>{t('No sources found matching your search.')}</p>
          </div>
        )}

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              type="button"
              className={styles.pageNav}
              disabled={currentPage === 1}
              onClick={() => goToPage(currentPage - 1)}
            >
              ← {t('Prev')}
            </button>

            {getPageWindow(currentPage, totalPages).map((item, i) =>
              item === '…' ? (
                <span key={`ellipsis-${i}`} className={styles.pageEllipsis}>
                  …
                </span>
              ) : (
                <button
                  key={item}
                  type="button"
                  className={cn(styles.pageItem, item === currentPage && styles.pageItemActive)}
                  onClick={() => goToPage(item)}
                >
                  {item}
                </button>
              ),
            )}

            <button
              type="button"
              className={styles.pageNav}
              disabled={currentPage === totalPages}
              onClick={() => goToPage(currentPage + 1)}
            >
              {t('Next')} →
            </button>
          </div>
        )}
      </div>
    </Section>
  );
};
